import type { UtmConfig, UtmPlatformConfig } from '../../../types/campaign';
import { PlaceholderHint } from './PlaceholderHint';

// UTM tag authoring (chunk D). Master toggle + per-platform checkboxes.
// When enabled, each checked platform shows the source/medium/term/content
// fields; a shared block provides campaign id/name. All string fields
// support template wildcards -- at AccountCampaign snapshot time (P2b)
// they're substituted per-location and appended to the resolved landing
// URL for that platform.

const EMPTY_PLATFORM: UtmPlatformConfig = {
  source: '',
  medium: '',
  term: '',
  content: '',
};

export type UtmConfigEditorProps = {
  value: UtmConfig;
  onChange: (next: UtmConfig) => void;
};

function togglePlatform(value: UtmConfig, platform: 'meta' | 'google'): UtmConfig {
  const isOn = value.platforms.includes(platform);
  const platforms = isOn
    ? value.platforms.filter((p) => p !== platform)
    : [...value.platforms, platform];
  const next: UtmConfig = { ...value, platforms };
  // Materialise the per-platform block when first enabled so the inputs
  // can bind. Drop it when disabled so stored docs stay clean.
  if (platform === 'meta') {
    next.meta = isOn ? null : (value.meta ?? { ...EMPTY_PLATFORM });
  } else {
    next.google = isOn ? null : (value.google ?? { ...EMPTY_PLATFORM });
  }
  return next;
}

function PlatformFields({
  label,
  value,
  onChange,
}: {
  label: string;
  value: UtmPlatformConfig;
  onChange: (next: UtmPlatformConfig) => void;
}) {
  const update = <K extends keyof UtmPlatformConfig>(key: K, v: string) => {
    onChange({ ...value, [key]: v });
  };
  return (
    <div className="border border-slate-200 rounded-md p-3 space-y-2">
      <h4 className="text-sm font-semibold text-slate-900">{label}</h4>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <label className="text-xs font-medium text-slate-700">utm_source</label>
          <input
            type="text"
            className="w-full rounded-md border border-slate-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
            value={value.source}
            onChange={(e) => update('source', e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium text-slate-700">utm_medium</label>
          <input
            type="text"
            className="w-full rounded-md border border-slate-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
            value={value.medium}
            onChange={(e) => update('medium', e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium text-slate-700">utm_term</label>
          <input
            type="text"
            className="w-full rounded-md border border-slate-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
            value={value.term}
            onChange={(e) => update('term', e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium text-slate-700">utm_content</label>
          <input
            type="text"
            className="w-full rounded-md border border-slate-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
            value={value.content}
            onChange={(e) => update('content', e.target.value)}
            placeholder="{suburbSlug}_offer"
          />
        </div>
      </div>
    </div>
  );
}

export function UtmConfigEditor({ value, onChange }: UtmConfigEditorProps) {
  const metaOn = value.platforms.includes('meta');
  const googleOn = value.platforms.includes('google');

  return (
    <div className="space-y-4">
      <label className="inline-flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={value.enabled}
          onChange={(e) => onChange({ ...value, enabled: e.target.checked })}
        />
        <span className="font-medium text-slate-900">Add UTM tags?</span>
      </label>

      {value.enabled && (
        <div className="space-y-4 border-l-2 border-slate-200 pl-4">
          <div className="space-y-2">
            <p className="text-xs text-slate-500">
              When enabled, UTM params are appended to each selected platform's resolved landing
              URL at rollout snapshot time. Template placeholders are substituted per-location.
            </p>
            <div className="flex flex-wrap gap-4">
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={metaOn}
                  onChange={() => onChange(togglePlatform(value, 'meta'))}
                />
                <span>Meta</span>
              </label>
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={googleOn}
                  onChange={() => onChange(togglePlatform(value, 'google'))}
                />
                <span>Google</span>
              </label>
            </div>
          </div>

          <div className="border border-slate-200 rounded-md p-3 space-y-2">
            <h4 className="text-sm font-semibold text-slate-900">Shared</h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-xs font-medium text-slate-700">utm_campaign (id)</label>
                <input
                  type="text"
                  className="w-full rounded-md border border-slate-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                  value={value.shared.campaignId ?? ''}
                  onChange={(e) =>
                    onChange({
                      ...value,
                      shared: { ...value.shared, campaignId: e.target.value || null },
                    })
                  }
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-slate-700">Campaign name (internal)</label>
                <input
                  type="text"
                  className="w-full rounded-md border border-slate-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                  value={value.shared.campaignName ?? ''}
                  onChange={(e) =>
                    onChange({
                      ...value,
                      shared: { ...value.shared, campaignName: e.target.value || null },
                    })
                  }
                />
              </div>
            </div>
          </div>

          {metaOn && value.meta && (
            <PlatformFields
              label="Meta"
              value={value.meta}
              onChange={(next) => onChange({ ...value, meta: next })}
            />
          )}
          {googleOn && value.google && (
            <PlatformFields
              label="Google"
              value={value.google}
              onChange={(next) => onChange({ ...value, google: next })}
            />
          )}

          <PlaceholderHint />
        </div>
      )}
    </div>
  );
}
